import React, { useEffect, useRef } from 'react';
import classes from './Home7.module.scss';
import bgImage from './bg-workspace.png';
import webDevImg from './web-dev.png';
import appDevImg from './app-dev.png';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Home7 = () => {
  const rootRef = useRef(null);
  const heroRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const indicatorRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let ctx = gsap.context(() => {
      // Hero Entrance Animation
      const tl = gsap.timeline();
      
      tl.to(`.${classes.companyName}`, { opacity: 1, y: 0, duration: 1, ease: 'power3.out', delay: 0.2 })
        .to(`.${classes.titleWrapper} .${classes.titleLine}`, { 
          opacity: 1, 
          y: 0, 
          duration: 1.2, 
          stagger: 0.2, 
          ease: 'power3.out' 
        }, '-=0.6')
        .to(`.${classes.subtitle}`, { opacity: 1, y: 0, duration: 1, ease: 'power3.out' }, '-=0.8')
        .to(indicatorRef.current, { opacity: 1, duration: 1 }, '-=0.4');

      // Parallax effect for the entire page background
      gsap.to(`.${classes.bgElements}`, {
        yPercent: 30, // Moves up as you scroll down
        ease: 'none',
        scrollTrigger: {
          trigger: rootRef.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1, /* Smooth out iOS scroll jitter */
        }
      });
      
      // Fading out hero content on scroll
      gsap.to(`.${classes.heroContent}`, {
        yPercent: 50,
        opacity: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        }
      });

      // Reveal first service card
      gsap.from(row1Ref.current, {
        y: 80,
        opacity: 0,
        duration: 1.2,
        ease: 'power4.out',
        scrollTrigger: {
          trigger: row1Ref.current,
          start: 'top 85%',
        }
      });

      // Reveal second service card
      gsap.from(row2Ref.current, {
        y: 80,
        opacity: 0,
        duration: 1.2,
        ease: 'power4.out',
        scrollTrigger: {
          trigger: row2Ref.current,
          start: 'top 85%',
        }
      });

      // Image parralax inside the cards
      const applyImageParallax = (ref) => {
        const img = ref.current.querySelector('img');
        if(img) {
          gsap.fromTo(img, 
            { yPercent: -10, scale: 1.1 },
            { 
              yPercent: 10,
              ease: 'none',
              scrollTrigger: {
                trigger: ref.current,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1
              }
            }
          );
        }
      };

      applyImageParallax(row1Ref);
      applyImageParallax(row2Ref);

    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className={classes.root} ref={rootRef}>
      <div className={classes.noise}></div>
      
      {/* Fixed Background Container */}
      <div className={classes.bgContainer}>
        <div className={classes.bgElements}>
          <div className={classes.bgImage} style={{ backgroundImage: `url(${bgImage})` }}></div>
          <div className={classes.orb1}></div>
          <div className={classes.orb2}></div>
          <div className={classes.orb3}></div>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className={classes.hero} ref={heroRef}>
        <div className={classes.heroContent}>
          <h2 className={classes.companyName} style={{ transform: 'translateY(20px)', opacity: 0 }}>
            <img src="/react-assets/logo.svg" alt="collabo Inc." className={classes.logoImg} />
          </h2>
          <div className={classes.titleWrapper}>
            <h1 className={classes.titleLine} style={{ transform: 'translateY(40px)', opacity: 0 }}>
              Design the
            </h1>
            <h1 className={classes.titleLine} style={{ transform: 'translateY(40px)', opacity: 0 }}>
              <span className={classes.highlight}>Future.</span>
            </h1>
          </div>
          <p className={classes.subtitle} style={{ transform: 'translateY(20px)', opacity: 0 }}>
            私たちは、美しく機能的な<br />
            ホームページとアプリケーションを開発しています。
          </p>
        </div>

        <div className={classes.scrollIndicator} ref={indicatorRef} style={{ opacity: 0 }}>
          <div className={classes.mouse}></div>
          <span className={classes.scrollText}>SCROLL</span>
        </div>
      </section>

      {/* Details Section */}
      <section className={classes.detailSection}>
        
        {/* Web Development */}
        <div className={classes.glassCard} ref={row1Ref}>
          <div className={classes.serviceRow}>
            <div className={classes.serviceText}>
              <div className={classes.serviceType}>Web Development</div>
              <h3 className={classes.serviceTitle}>ホームページ制作</h3>
              <p className={classes.serviceDesc}>
                最新のデザイントレンドとテクノロジーを駆使し、
                ブランドの魅力を最大限に引き立てるWebサイトを構築します。
                視覚的な美しさと使いやすさを両立した、成果につながるデザインを提供します。
              </p>
            </div>
            <div className={classes.serviceImageWrapper}>
              <img src={webDevImg} alt="ホームページ制作" className={classes.serviceImage} />
            </div>
          </div>
        </div>

        {/* Space revealing parallax background */}
        <div className={classes.spacer}></div>

        {/* App Development */}
        <div className={classes.glassCard} ref={row2Ref}>
          <div className={`${classes.serviceRow} ${classes.reverse}`}>
            <div className={classes.serviceText}>
              <div className={classes.serviceType}>App Development</div>
              <h3 className={classes.serviceTitle}>アプリ開発</h3>
              <p className={classes.serviceDesc}>
                iOS・Android向けのネイティブアプリからWebアプリまで、
                ユーザーの日常に溶け込む快適なアプリケーションを開発します。
                直感的なUIと滑らかなアニメーションで、最高の体験を創出します。
              </p>
            </div>
            <div className={classes.serviceImageWrapper}>
              <img src={appDevImg} alt="アプリ開発" className={classes.serviceImage} />
            </div>
          </div>
        </div>
        
      </section>

      {/* Space before footer to show parallax */}
      <div className={classes.footerSpacer}></div>

      <footer className={classes.footer}>
        <div className={classes.footerInner}>
          <div className={classes.footerLogo}>
             <img src="/react-assets/logo.svg" alt="collabo Inc." className={classes.footerLogoImg} />
          </div>
          <div className={classes.footerAddress}>
            〒106-0032 東京都港区六本木X-X-X<br/>
            TEL: 03-XXXX-XXXX<br/>
            Email: contact@collabo.example.com
          </div>
          <p className={classes.copyright}>&copy; {new Date().getFullYear()} collabo Inc. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home7;
